import { ProfileDto } from '@diritto/common/account/dtos/profile.dto';
import { UserPublicDto } from '@diritto/common/account/dtos/user-public.dto';
import { TWITTER_URL } from '@diritto/common/constants/url';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { convertToDotString } from '@diritto/common/utils/date';
import styled from '@emotion/styled';
import { ModalType } from 'constants/modal';
import { useTrackingState } from 'contexts/TrackingProvider';
import { BlockIcon } from 'icons/BlockIcon';
import { HelpIcon } from 'icons/HelpIcon';
import { LinkIcon } from 'icons/LinkIcon';
import { EmailLoginIcon } from 'icons/login/EmailLoginIcon';
import { TwitterLoginIcon } from 'icons/login/TwitterLoginIcon';
import { DateTime } from 'luxon';
import { ProfileImage } from 'app-components/accounts/profiles/ProfileImage';
import React, { useCallback, useMemo } from 'react';
import { ExternalLink } from 'ui-components/common/ExternalLink';
import { IconButton } from 'ui-components/common/IconButton';
import { Link } from 'ui-components/common/Link';
import { useCopyText } from 'ui-hooks/useCopyText';
import { showToast } from 'utils/modal';
import { ModalHandler } from 'utils/ModalHandler';
import { configure } from 'utils/configure';
import { PartnerDto } from '@diritto/common/partner/dtos/partner.dto';

interface ProfileProps {
  className?: string;
  profile: ProfileDto;
  user: UserPublicDto;
  partner?: PartnerDto;
}

export const Profile = ({ className, profile, user, partner }: ProfileProps) => {
  const copyText = useCopyText();
  const { tracker } = useTrackingState();
  const isAbuser = useMemo(() => {
    if (user?.abuser) {
      const { disabledStartAt, disabledEndAt } = user.abuser;
      const start = DateTime.fromJSDate(new Date(disabledStartAt)).diffNow().toMillis();
      const end = DateTime.fromJSDate(new Date(disabledEndAt)).diffNow().toMillis();
      return start < 0 && end > 0;
    }
    return false;
  }, [user.abuser]);

  const handleClickEmailCopy = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      tracker.track(TrackingEventType.ClickProfileEmailCopy, { profileId: profile.id });
      e.preventDefault();
      try {
        await copyText(profile.email || '');
        showToast('이메일이 복사되었어요.');
      } catch {
        showToast('이메일 복사에 실패했어요.');
      }
    },
    [copyText, profile.email, profile.id, tracker],
  );

  const handleClickBlockHelp = useCallback(() => {
    const endDate = user?.abuser?.disabledEndAt ? convertToDotString(user.abuser.disabledEndAt, false) : '';
    ModalHandler.show(ModalType.Alert, {
      title: '20회 이상의 신고로 이용이 제재된 프로필이에요.',
      description: `${endDate}까지 이벤트 참여 또는 이벤트 리워드 획득, 댓글 작성, 작품 생성을 할 수 없어요.`,
    });
  }, [user?.abuser?.disabledEndAt]);

  const handleClickTwitter = useCallback(() => {
    tracker.track(TrackingEventType.ClickProfileTwitter, { profileId: profile.id });
  }, [profile.id, tracker]);

  const handleClickEmail = useCallback(() => {
    tracker.track(TrackingEventType.ClickProfileEmail, { profileId: profile.id });
  }, [profile.id, tracker]);

  return (
    <StyledContainer className={className}>
      <StyledWrapper>
        <ProfileImage file={profile?.profileImage} defaultImageKey={profile?.nickname} size={80} />
        <StyledProfileMeta>
          <StyledNickname>
            {profile?.nickname}{' '}
            {partner?.verifiedAt && (
              <img
                src={`${configure.staticPrefix}/images/partner/verified.svg`}
                alt="Contents Partners"
                className="w-[18px]"
              />
            )}
          </StyledNickname>

          {profile?.email && (
            <StyledSNSWrapper>
              <Link href={`mailto:${profile.email}`} onClick={handleClickEmail}>
                <StyledSNSIconWrapper>
                  <EmailLoginIcon color={Color.Gray400} />
                </StyledSNSIconWrapper>
                <p>{profile.email}</p>
                <IconButton onClick={handleClickEmailCopy}>
                  <LinkIcon className="size-[16px]" />
                </IconButton>
              </Link>
            </StyledSNSWrapper>
          )}
          {profile?.twitter && (
            <StyledSNSWrapper>
              <ExternalLink href={`${TWITTER_URL}/${profile.twitter}`} onClick={handleClickTwitter}>
                <StyledSNSIconWrapper>
                  <TwitterLoginIcon color={Color.Gray400} />
                </StyledSNSIconWrapper>
                <p>{profile?.twitter}</p>
              </ExternalLink>
            </StyledSNSWrapper>
          )}
          {isAbuser && (
            <StyledSNSWrapper>
              <button type="button" onClick={handleClickBlockHelp}>
                <StyledSNSIconWrapper>
                  <BlockIcon color={Color.Gray400} />
                </StyledSNSIconWrapper>
                <p>이용 제재됨</p>
                <StyledSNSIconWrapper>
                  <HelpIcon color={Color.Gray400} />
                </StyledSNSIconWrapper>
              </button>
            </StyledSNSWrapper>
          )}
        </StyledProfileMeta>
      </StyledWrapper>
      {profile?.description && <StyledDescription>{profile?.description}</StyledDescription>}
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
`;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

const StyledProfileMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StyledNickname = styled.p`
  font-weight: ${FontWeight.Bold};
  ${Typography.Medium};
  word-break: break-all;
`;

const StyledDescription = styled.p`
  ${Typography.XXSmall};
  color: ${Color.Gray700};
  font-weight: ${FontWeight.Medium};
  width: 100%;
  word-break: break-all;
`;

const StyledSNSIconWrapper = styled.div`
  display: flex;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
`;

const StyledSNSWrapper = styled.div`
  & > * {
    display: flex;
    align-items: center;
    ${Typography.XXSmall};
    gap: 4px;

    & > p {
      color: ${Color.Gray700};
      font-weight: ${FontWeight.Medium};
      ${Typography.XXXSmall};
    }
  }
`;
